import { ReactNode } from 'react';

import { IconAngleLeft } from '@iwoca/icons';
import { IwocaLogo } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';

import styles from './MobileSignupLayout.module.css';
import { Button } from '../../Button/Button';

type TMobileSignupLayoutProps = {
  /** 1-based position + total, drives the progress bar and "Step X of N". */
  progress: { current: number; total: number };
  children: ReactNode;
  onBack?: () => void;
};

/**
 * The constant outer frame for the whole mobile signup flow (back + logo +
 * progress). Rendered once by the container and reconciled in place across step
 * changes, so this chrome never remounts — only `children` (the step) animates.
 */
export const MobileSignupLayout = ({
  progress,
  children,
  onBack,
}: TMobileSignupLayoutProps) => {
  const { t } = useLingui();
  const { current, total } = progress;
  const filledPercentage = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        {onBack && (
          <Button
            variant="tertiary"
            size="small"
            iconOnly
            onClick={onBack}
            aria-label={t`Back`}
            className={styles.back}
          >
            <IconAngleLeft />
          </Button>
        )}
        <IwocaLogo color="red" className={styles.logo} />
      </header>

      <div className={styles.progress}>
        <div className={styles.track}>
          <div
            className={styles.bar}
            style={{ width: `${filledPercentage}%` }}
            role="progressbar"
            aria-valuenow={current}
            aria-valuemin={1}
            aria-valuemax={total}
          />
        </div>
        <span
          className={styles.stepCount}
        >{t`Step ${current} of ${total}`}</span>
      </div>

      {children}
    </div>
  );
};
